/**
 * fieldLabels — the single source of humanized enum labels for the pass designer
 * (§2.1). Each array is a ReadonlyArray<{ value; label; example? }> in canonical
 * order: `value` is the raw PK* string PUT to the backend UNCHANGED; only `label`
 * and `example` are Hungarian. Also hosts the custom-field key helpers (§2.2):
 * slugifyKey (label → safe key) + dedupeKeyInGroup (collision suffix).
 *
 * NOTE: the seed-key lookup `liveKeySetForGroup` is NOT here — it already lives in
 * `lib/fieldEditing.ts` and is imported from there by every consumer (including
 * the group editor's auto-slug handler). Do not re-declare it in this module.
 */

export interface FieldOption {
  value: string;
  label: string;
  example?: string;
}

export const ALIGNMENT_OPTIONS: ReadonlyArray<FieldOption> = [
  { value: 'PKTextAlignmentLeft', label: 'Balra' },
  { value: 'PKTextAlignmentCenter', label: 'Középre' },
  { value: 'PKTextAlignmentRight', label: 'Jobbra' },
  { value: 'PKTextAlignmentNatural', label: 'Automatikus (nyelv szerint)' },
];

export const DATE_STYLE_OPTIONS: ReadonlyArray<FieldOption> = [
  { value: 'PKDateStyleNone', label: 'Ne jelenjen meg' },
  { value: 'PKDateStyleShort', label: 'Rövid', example: '2026.07.01.' },
  { value: 'PKDateStyleMedium', label: 'Közepes', example: '2026. júl. 1.' },
  { value: 'PKDateStyleLong', label: 'Hosszú', example: '2026. július 1.' },
  { value: 'PKDateStyleFull', label: 'Teljes', example: '2026. július 1., szerda' },
];

export const TIME_STYLE_OPTIONS: ReadonlyArray<FieldOption> = [
  { value: 'PKDateStyleNone', label: 'Ne jelenjen meg' },
  { value: 'PKDateStyleShort', label: 'Rövid', example: '14:45' },
  { value: 'PKDateStyleMedium', label: 'Közepes', example: '14:45:30' },
  { value: 'PKDateStyleLong', label: 'Hosszú', example: '14:45:30 CEST' },
  { value: 'PKDateStyleFull', label: 'Teljes', example: '14:45:30 közép-európai nyári idő' },
];

export const NUMBER_STYLE_OPTIONS: ReadonlyArray<FieldOption> = [
  { value: 'PKNumberStyleDecimal', label: 'Tizedes szám', example: '1 234,5' },
  { value: 'PKNumberStylePercent', label: 'Százalék', example: '123 450%' },
  { value: 'PKNumberStyleScientific', label: 'Tudományos', example: '1,2345E3' },
  {
    value: 'PKNumberStyleSpellOut',
    label: 'Kiírva szöveggel',
    example: 'ezerkétszázharmincnégy egész öt',
  },
];

export const BARCODE_FORMAT_OPTIONS: ReadonlyArray<FieldOption> = [
  { value: 'PKBarcodeFormatQR', label: 'QR-kód' },
  { value: 'PKBarcodeFormatPDF417', label: 'PDF417 (vonalkód)' },
  { value: 'PKBarcodeFormatAztec', label: 'Aztec-kód' },
  { value: 'PKBarcodeFormatCode128', label: 'Code 128 (vonalkód)' },
];

export const TRANSIT_TYPE_OPTIONS: ReadonlyArray<FieldOption> = [
  { value: 'PKTransitTypeAir', label: 'Repülő' },
  { value: 'PKTransitTypeTrain', label: 'Vonat' },
  { value: 'PKTransitTypeBus', label: 'Busz' },
  { value: 'PKTransitTypeBoat', label: 'Hajó' },
  { value: 'PKTransitTypeGeneric', label: 'Egyéb' },
];

export function slugifyKey(label: string): string {
  const slug = label
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug.length > 0 ? slug : 'mezo';
}

export function dedupeKeyInGroup(existingKeys: ReadonlyArray<string>, base: string): string {
  if (!existingKeys.includes(base)) return base;
  let n = 2;
  while (existingKeys.includes(`${base}-${String(n)}`)) n += 1;
  return `${base}-${String(n)}`;
}
